import { useState, useEffect } from "react";
import { callWithAuth, submitOnboarding } from "@/api/Api.ts";
import {
  Onboarding,
  LearningReason,
  LanguageLearningChallenge,
  LanguageLevelOnboarding,
  PreferredTopic,
} from "@shared_types";

export type Months = number;
export type Minutes = number;

// Type for onboarding data with specific field types
export type OnboardingFormData = {
  whyDoYouWantToLearn: LearningReason;
  whenDoYouWantToBeFluentBy: Months;
  howMuchCanYouStudyEachDay: Minutes;
  whatIsYourBiggestChallengeWithLearningLanguage: LanguageLearningChallenge;
  currentSpanishLevel: LanguageLevelOnboarding;
  preferredTopic: PreferredTopic;
};

// Initial state with typed values
const initialOnboardingState: OnboardingFormData = {
  whyDoYouWantToLearn: "no_value",
  // default values
  whenDoYouWantToBeFluentBy: 12,
  // default values
  howMuchCanYouStudyEachDay: 15,
  whatIsYourBiggestChallengeWithLearningLanguage: "no_value",
  currentSpanishLevel: "no_value",
  preferredTopic: "no_value",
};

export const useOnboarding = () => {
  const [onboardingData, setOnboardingData] = useState<OnboardingFormData>(
    () => {
      // Try to load from localStorage on initialization
      const savedData = localStorage.getItem("onboardingData");
      return savedData ? JSON.parse(savedData) : initialOnboardingState;
    }
  );

  // Save to localStorage whenever onboardingData changes
  useEffect(() => {
    localStorage.setItem("onboardingData", JSON.stringify(onboardingData));
  }, [onboardingData]);

  // Update a specific field in the onboarding data
  const updateOnboardingField = <K extends keyof OnboardingFormData>(
    field: K,
    value: OnboardingFormData[K]
  ) => {
    setOnboardingData((prev) => {
      const newData = { ...prev, [field]: value };
      return newData;
    });
  };

  // Submit onboarding data to the server
  const submitOnboardingData = async (): Promise<void> => {
    try {
      await callWithAuth(submitOnboarding, onboardingData as Onboarding);
    } catch (error) {
      console.error("Error submitting onboarding data:", error);
      throw error;
    }
  };

  // Reset onboarding data to initial state
  const resetOnboarding = () => {
    setOnboardingData(initialOnboardingState);
    localStorage.removeItem("onboardingData");
  };

  return {
    onboardingData,
    updateOnboardingField,
    submitOnboarding: submitOnboardingData,
    resetOnboarding,
  };
};
